import React, { PropsWithChildren } from 'react';
import { Box, IconButton, styled } from '@mui/material';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AccountMenu from 'components/AccountMenu/AccountMenu';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { NavbarItem, TabItem } from 'features/navbar/types';
import { NavList } from '../NavList/NavList';
import { TabletDesktopNavbarContainer } from './TabletDesktopNavbarContainer';
import { NavbarLogo } from './NavbarLogo';

interface DesktopNavbarProps {
  brandLogo: React.ReactElement;
  onBrandLogoClick: () => void;
  selectedTab?: NavbarItem;
  onTabChange: (tabObject: TabItem) => void;
  desktopNavitemList: TabItem[];
  tabletNavitemList: TabItem[];
}

const StyledNavbarLogo = styled(NavbarLogo)(({ theme }) => ({
  paddingLeft: theme.spacing(1.375),
  paddingRight: theme.spacing(1.375),
}));

const LogoSection = styled(Box)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  display: 'flex',
  flex: isDesktop ? 'none' : 1,
  justifyContent: 'flex-start',
}));

const TabsSection = styled(Box)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  display: 'flex',
  flex: isDesktop ? 'auto' : 1,
  justifyContent: isDesktop ? 'flex-start' : 'center',
}));

const AccountSection = styled(Box)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  display: 'flex',
  flex: isDesktop ? 'none' : 1,
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

export const DesktopTabletNavbar = ({
  brandLogo,
  onBrandLogoClick,
  onTabChange,
  selectedTab,
  desktopNavitemList,
  tabletNavitemList,
}: PropsWithChildren<DesktopNavbarProps>) => {
  const { isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const { isTaskModalExpanded, setIsTaskModalExpanded } = useLayoutElements();
  const navItemList = isDesktop ? desktopNavitemList : tabletNavitemList;

  return (
    <TabletDesktopNavbarContainer toolbarStyle={{ justifyContent: 'space-between' }}>
      <LogoSection isDesktop={isDesktop}>
        <StyledNavbarLogo brandLogo={brandLogo} onBrandLogoClick={onBrandLogoClick} />
      </LogoSection>
      <TabsSection isDesktop={isDesktop}>
        <NavList
          hideTabIndicator={!isDesktop}
          onTabChange={onTabChange}
          selectedTab={selectedTab}
          navItemList={navItemList}
        />
      </TabsSection>
      <AccountSection isDesktop={isDesktop}>
        <AccountMenu />
        {isTaskModalExpanded && (
          <IconButton
            className="navbar__collapse-task-modal-button"
            onClick={() => setIsTaskModalExpanded(false)}
            aria-label={translation('exit_full_screen_accessibility')}
            sx={{ color: 'grey.800', ml: 1, mr: 2, fontSize: 'small' }}
          >
            <CloseFullscreenIcon aria-hidden focusable={false} />
          </IconButton>
        )}
      </AccountSection>
    </TabletDesktopNavbarContainer>
  );
};
